'use client'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { ArrowLeft } from 'lucide-react'
import ShopmnLogo from '@/assets/logo-white.svg'
import Bubble1 from '@/assets/auth-bubble-1.svg'
import Bubble2 from '@/assets/auth-bubble-2.svg'
import Bubble3 from '@/assets/auth-bubble-3.svg'
import Bubble4 from '@/assets/auth-bubble-4.svg'

type AuthLayoutProps = {
  children: React.ReactNode
  vendor?: any
  quote?: string
  quoteAuthor?: string
  image?: string
}

const BUBBLES = [Bubble1, Bubble2, Bubble3, Bubble4]

function ScrollRow({ reverse, offset = 0 }: { reverse?: boolean, offset?: number }) {
  const items = Array.from({ length: 13 }, (_, i) => (
    <Image key={i} src={BUBBLES[(i + offset) % 4]} alt="" className="size-18 shrink-0 rounded-full" />
  ))
  return (
    <div className="flex items-center gap-5">
      <div className={`relative flex items-center gap-5 animate-infinite-scroll ${reverse ? 'direction-reverse' : ''}`}>
        {items}
      </div>
      <div className={`relative flex items-center gap-5 animate-infinite-scroll ${reverse ? 'direction-reverse' : ''}`}>
        {items}
      </div>
    </div>
  )
}

export default function AuthLayout({ children, vendor, quote }: AuthLayoutProps) {
  const pathname = usePathname() || ''
  const vendorName = vendor?.name || 'Shop.mn'
  const blurb = quote || vendor?.description || 'Анхны хиймэл оюун ухаант цахим дэлгүүрээ нэг минутанд нээж, бизнесээ эхлүүлээрэй.'

  return (
    <div className="min-h-screen flex" style={{ background: '#08080a' }}>
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden" style={{ flex: '0 0 50%', background: '#0c0c10', borderRight: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="grow basis-0 min-h-0 p-10 flex flex-col gap-10 relative z-10">
          <a href="https://shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 shrink-0">
            <Image src={ShopmnLogo} alt="Shop.mn" className="h-7 w-auto" />
          </a>
          <div className="min-h-0 overflow-hidden mt-auto mb-auto">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
              {vendorName}
            </div>
            <blockquote
              className="mt-5 font-medium tracking-[-0.02em] leading-[1.15] text-white italic line-clamp-5"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(22px, 2.5vw, 32px)' }}
            >
              &ldquo;{blurb}&rdquo;
            </blockquote>
          </div>
        </div>
        <div className="w-full grow basis-0">
          <div className="relative size-full overflow-hidden">
            <div className="absolute bottom-0 left-1/2 size-20 -translate-x-1/2 translate-y-1/2 scale-x-[1.6] mix-blend-screen">
              <div className="absolute -inset-16 mix-blend-screen blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
              <div className="absolute -inset-16 mix-blend-screen blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
            </div>
            <div className="absolute bottom-0 left-1/2 size-20 -translate-x-1/2 translate-y-1/2 scale-x-[1.6] opacity-25">
              <div className="absolute -inset-16 mix-blend-screen blur-[50px] saturate-[2] bg-[conic-gradient(from_90deg,#F00_5deg,#EAB308_63deg,#5CFF80_115deg,#1E00FF_170deg,#855AFC_220deg,#3A8BFD_286deg,#F00_360deg)]" />
            </div>
            <div className="relative isolate size-full">
              <div className="relative size-full opacity-90 mask-intersect mask-[linear-gradient(#000f_50%,#0006),linear-gradient(90deg,#000f_50%,#000a)]">
                <div className="translate-y-[30%] skew-y-[-16deg]">
                  <div className="flex flex-col gap-7">
                    <ScrollRow offset={0} reverse />
                    <ScrollRow offset={1} />
                    <ScrollRow offset={2} reverse />
                    <ScrollRow offset={3} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col" style={{ background: '#08080a' }}>
        <div className="flex items-center justify-between px-6 lg:px-10 py-6 lg:py-8 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="inline-flex items-center gap-1.5 transition-colors hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" />
            Нүүр хуудас руу буцах
          </a>
          <div className="lg:hidden">
            {vendor?.logo ? (
              <span className="inline-flex items-center text-white font-semibold text-[14px]">{vendorName}</span>
            ) : (
              <span className="inline-flex items-center text-white font-semibold text-[14px]" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{vendorName}</span>
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-10 pb-10">
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>
        <div className="px-6 lg:px-10 py-6 text-[11px] flex flex-wrap justify-between gap-2" style={{ color: 'rgba(255,255,255,0.4)', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <span>© {new Date().getFullYear()} {vendorName}</span>
          <div className="flex gap-5">
            <a href={navigateUrl('/service', pathname)} className="hover:text-white transition-colors">Үйлчилгээний нөхцөл</a>
            <a href={navigateUrl('/payment', pathname)} className="hover:text-white transition-colors">Нууцлал</a>
          </div>
        </div>
      </div>
    </div>
  )
}
